import React, { useMemo } from 'react';
import { useLotsCommerciaux } from '../../../contexts/lotCommercialContext';
import { useLots } from '../../../contexts/lotContext';

const IDS_NATURE = {
    APPARTEMENT: 1,
    MAISON_INDIVIDUELLE: 2,
    PARKING: 3,
    GARAGE: 4,
    CAVE: 5,
    BALCON: 6,
    TERRASSE: 7,
    JARDIN: 8,
    LOGGIA: 9,
    AUTRE: 10,
    MAISON_INDIVIDUELLE_CONSTRUCTEUR: 12,
    COMMERCE: 13,
    BUREAUX: 14,
    TERRAIN: 15,
    HOTEL: 16,
    CRECHE: 17,
    GYMNASE: 18,
    ECOLE: 19,
    ENTREPOT: 20,
    BÂTIMENT_INDUSTRIEL: 21,
    LOCAUX_D_ACTIVITES: 22,
    CHAMBRE_D_EQUIPEMENT_DE_SANTE: 23,
    POLE_MEDICAL: 24,
    AMENAGEMENT: 25
};

const ArrayLots = () => {

    // CONTEXTS
    const { lots } = useLots();
    const { lotCommercialActif } = useLotsCommerciaux();
    const { tva } = lotCommercialActif || {};

    // CONSTANTES
    const numberFormat = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

    // MEMOS
    // KP - 2772 - 17/09/2024 : rustine cause manque de temps ; recalculer le prix HT du lot principal si le prix HT de la réservation est différent de celui du lot commercial
    // Reprendre le même fonctionnement que pour le publipostage
    const _updatedLots = useMemo(() => {

        if (!Array.isArray(lots)) return [];
        if (!lotCommercialActif) return [];
        if (lots.lenght === 0) return lots;

        const prixHtReservation = lotCommercialActif.prixHT;
        const prixHtLotCommercial = lotCommercialActif.prixHTInitial;
        if (prixHtLotCommercial === prixHtReservation) return lots;

        let typeModification;
        let delta = 0;
        if (prixHtReservation < prixHtLotCommercial) {
            delta = prixHtLotCommercial - prixHtReservation;
            typeModification = "Remise";
        }
        else if (prixHtReservation > prixHtLotCommercial) {
            delta = prixHtReservation - prixHtLotCommercial;
            typeModification = "Augmentation";
        }

        const lotsPrincipaux = lots.filter(lot => lot.idNature === IDS_NATURE.APPARTEMENT || lot.idNature === IDS_NATURE.MAISON_INDIVIDUELLE);
        let nbLots = lotsPrincipaux.length;
        if (nbLots <= 0) nbLots = lots.lenght;

        return lots.map(lot => {
            const lotPrixHt = lot.prixHT;
            let prixTtc = lotPrixHt * (1 + tva);
            if (lot.idNature === IDS_NATURE.APPARTEMENT || lot.idNature === IDS_NATURE.MAISON_INDIVIDUELLE || lots.lenght === 1) {
                const prixHtCalcule = typeModification === "Remise"
                    ? lotPrixHt - delta / nbLots
                    : lotPrixHt + delta / nbLots;
                prixTtc = prixHtCalcule * (1 + tva);
            }
            return { ...lot, prixTtc };
        });

    }, [lotCommercialActif, lots, tva]);

    // UI
    const styles = useMemo(
        () => {
            const cell = { minWidth: 120, maxWidth: 120 };
            const cellSm = { minWidth: 60, maxWidth: 60 };
            const cellXLg = { minWidth: 240, maxWidth: 240 };

            let styles = {
                body: {
                    borderBottom: "2px solid #D9D9D9",
                    borderTop: "2px solid #D9D9D9",
                },
                cell,
                cellRight: { ...cell, textAlign: "right" },
                cellSm,
                cellSmCenter: { ...cellSm, textAlign: "center" },
                cellSmRight: { ...cellSm, textAlign: "right" },
                cellXLg,
                header: {
                    background: "white",
                    color: "red",
                    height: 50,
                    textAlign: "left",
                    textTransform: "uppercase",
                    //verticalAlign: "bottom"
                },
                row: {
                    color: "#8A8A8A",
                    fontSize: 16,
                    fontWeight: 400,
                    height: 50,
                    lineHeight: "19px"
                },
                outer: { display: "grid" },
                table: {
                    borderCollapse: "collapse",
                    display: "block",
                    marginRight: 1,
                    overflowX: "auto",
                    paddingBottom: 25,
                    whiteSpace: "nowrap"
                }
            };

            return styles;
        },
        []
    );

    // RENDER
    return <div style={styles.outer}>
        <table style={styles.table}>
            <thead style={styles.header}>
                <tr>
                    <th style={styles.cellXLg}>n° lot</th>
                    <th style={styles.cell}>type</th>
                    <th style={styles.cellSm}>étage</th>
                    <th style={styles.cellSmCenter}>bât.</th>
                    <th style={styles.cellSmCenter}>expo.</th>
                    <th style={styles.cellSmRight}>sha</th>
                    <th style={styles.cellRight}>prix ttc</th>
                </tr>
            </thead>
            <tbody style={styles.body}>
                {Array.isArray(_updatedLots) && _updatedLots.map((lot, index) => {
                    const { numero, lotType, etage, batiment, exposition, surfaceHabitable, prixTtc } = lot;

                    let trStyle = { ...styles.row };
                    if (index % 2 === 1) trStyle.background = "#F6F6F6";
                    else trStyle.background = "white";

                    return <tr key={index} style={trStyle}>
                        <td style={styles.cellXLg}>{numero}</td>
                        <td style={styles.cell}>{lotType}</td>
                        <td style={styles.cellSm}>{etage}</td>
                        <td style={styles.cellSmCenter}>{batiment}</td>
                        <td style={styles.cellSmCenter}>{exposition}</td>
                        <td style={styles.cellSmRight}>{!!surfaceHabitable ? `${surfaceHabitable}\xa0m²` : null}</td>
                        <td style={styles.cellRight}>{numberFormat.format(prixTtc)}</td>
                    </tr>;
                })}
            </tbody>
        </table>
    </div>;

};

export default ArrayLots;